import { authApi } from '@/features/auth/authApi';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import { clearUserAuthStorage, getStorageItem } from '../helpers/storage';
import { baseApiQuery } from './baseQuery';
import { PUBLIC_USER_CREDENTIALS, TOKEN_COOKIE_NAMES } from './constants';

const mutex = new Mutex();

// This is an official Redux Toolkit example and their recommendation for mutex library
const customApiFetchBase: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  await mutex.waitForUnlock();
  let result = await baseApiQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshResult = await tryRefresh(0, args, api, extraOptions);
        if (refreshResult) {
          result = refreshResult;
        } else {
          clearUserAuthStorage();
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseApiQuery(args, api, extraOptions);
    }
  }

  return result;
};

const tryRefresh = async (counter, args, api, extraOptions) => {
  // try 2 times. ensures that 2 attempts are not valid because the tokens that have been refresh by other query are also invalid
  if (counter == 2) {
    return null;
  } else {
    const creds = getStorageItem(PUBLIC_USER_CREDENTIALS);
    const refreshToken = getStorageItem(TOKEN_COOKIE_NAMES['refresh']);

    const [userCreds, rToken] = await Promise.all([creds, refreshToken]);

    if (!userCreds || !rToken) {
      return null;
    }

    const refreshResult = await api.dispatch(
      authApi.endpoints.handleRefreshingTheToken.initiate({
        creds: userCreds && JSON.parse(userCreds),
        refreshToken: rToken
      })
    );

    if ('data' in refreshResult) {
      return await baseApiQuery(args, api, extraOptions);
    } else {
      return tryRefresh(++counter, args, api, extraOptions);
    }
  }
};

export default customApiFetchBase;
